import { useState, forwardRef, useImperativeHandle } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Building2, Rocket, Mail, Users, ArrowLeft } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { z } from "zod";
import { useLanguage } from "@/contexts/LanguageContext";
import { motion, AnimatePresence } from "framer-motion";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

const contactSchema = z.object({
  name: z.string().trim().min(1, { message: "Name is required" }).max(100),
  email: z.string().trim().email({ message: "Invalid email address" }).max(255),
  company: z.string().trim().min(1, { message: "Company is required" }).max(100),
  message: z.string().trim().min(10, { message: "Message must be at least 10 characters" }).max(1000),
});

type UserType = "corporate" | "startup" | "ecosystem" | null;

export interface ContactDialogRef {
  open: () => void;
}

const Contact = forwardRef<ContactDialogRef, {}>((props, ref) => {
  const { t } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [userType, setUserType] = useState<UserType>(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    message: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const { toast } = useToast();

  useImperativeHandle(ref, () => ({
    open: () => setIsOpen(true),
  }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      contactSchema.parse(formData);
      setErrors({});
      
      // Send email via edge function
      const { supabase } = await import("@/integrations/supabase/client");
      const { data, error } = await supabase.functions.invoke("send-contact-email", {
        body: {
          ...formData,
          userType,
        },
      });

      if (error) {
        console.error("Error sending email:", error);
        toast({
          title: "Error",
          description: "Failed to send message. Please try again.",
          variant: "destructive",
        });
        return;
      }
      
      // Show success animation
      setIsSubmitted(true);
      
      // Reset form after delay
      setTimeout(() => {
        setFormData({ name: "", email: "", company: "", message: "" });
        setUserType(null);
        setIsSubmitted(false);
        setIsOpen(false);
      }, 3000);
    } catch (error) {
      if (error instanceof z.ZodError) {
        const newErrors: Record<string, string> = {};
        error.errors.forEach((err) => {
          if (err.path[0]) {
            newErrors[err.path[0].toString()] = err.message;
          }
        });
        setErrors(newErrors);
      } else {
        console.error("Unexpected error:", error);
        toast({
          title: "Error",
          description: "An unexpected error occurred. Please try again.",
          variant: "destructive",
        });
      }
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen} modal={true}>
      <DialogTrigger asChild>
        <Button size="lg" className="gap-2">
          <Mail className="h-5 w-5" />
          {t("nav.contact")}
        </Button>
      </DialogTrigger>
      <DialogContent 
        className="max-w-3xl max-h-[90vh] overflow-y-auto bg-background/95 backdrop-blur-xl border-border/50"
        onOpenAutoFocus={(e) => e.preventDefault()}
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <DialogHeader className="space-y-3">
          <DialogTitle className="text-3xl font-black tracking-tight">{t("contact.title")}</DialogTitle>
          <DialogDescription className="text-base">
            {t("contact.description")}
          </DialogDescription>
        </DialogHeader>
        
        <div className="mt-6">
          <AnimatePresence mode="wait">
            {isSubmitted ? (
              <motion.div
                key="success"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                className="flex flex-col items-center justify-center py-16 px-8"
              >
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
                  className="relative mb-8"
                >
                  <div className="w-24 h-24 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center">
                    <svg
                      className="w-12 h-12 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <motion.path
                        initial={{ pathLength: 0 }}
                        animate={{ pathLength: 1 }}
                        transition={{ delay: 0.4, duration: 0.5 }}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                  <motion.div
                    initial={{ scale: 1, opacity: 0.5 }}
                    animate={{ scale: 1.5, opacity: 0 }}
                    transition={{ duration: 1, repeat: Infinity }}
                    className="absolute inset-0 rounded-full bg-primary/30"
                  />
                </motion.div>
                <motion.h3
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 }}
                  className="text-2xl font-bold text-foreground mb-3"
                >
                  {t("contact.success.title")}
                </motion.h3>
                <motion.p
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.7 }}
                  className="text-muted-foreground text-center max-w-md"
                >
                  {t("contact.success.description")}
                </motion.p>
              </motion.div>
            ) : !userType ? (
              <motion.div
                key="selection"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="grid grid-cols-1 md:grid-cols-3 gap-6 py-4"
              >
                <motion.button
                  whileHover={{ scale: 1.05, y: -5 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setUserType("corporate")}
                  className="group relative p-8 rounded-3xl border-2 border-border bg-card overflow-hidden transition-all duration-300"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <div className="relative flex flex-col items-center text-center">
                    <motion.div
                      whileHover={{ rotate: 5 }}
                      className="p-5 rounded-2xl bg-gradient-to-br from-primary/10 to-primary/5 mb-6 group-hover:from-primary/20 group-hover:to-primary/10 transition-all duration-300"
                    >
                      <Building2 className="w-12 h-12 text-primary" />
                    </motion.div>
                    <h3 className="text-xl font-bold mb-3 text-foreground">
                      {t("contact.corporate.title")}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {t("contact.corporate.description")}
                    </p>
                  </div>
                </motion.button>

                <motion.button
                  whileHover={{ scale: 1.05, y: -5 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setUserType("startup")}
                  className="group relative p-8 rounded-3xl border-2 border-border bg-card overflow-hidden transition-all duration-300"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-accent/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <div className="relative flex flex-col items-center text-center">
                    <motion.div
                      whileHover={{ rotate: -5 }}
                      className="p-5 rounded-2xl bg-gradient-to-br from-accent/10 to-accent/5 mb-6 group-hover:from-accent/20 group-hover:to-accent/10 transition-all duration-300"
                    >
                      <Rocket className="w-12 h-12 text-accent" />
                    </motion.div>
                    <h3 className="text-xl font-bold mb-3 text-foreground">
                      {t("contact.startup.title")}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {t("contact.startup.description")}
                    </p>
                  </div>
                </motion.button>

                <motion.button
                  whileHover={{ scale: 1.05, y: -5 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setUserType("ecosystem")}
                  className="group relative p-8 rounded-3xl border-2 border-border bg-card overflow-hidden transition-all duration-300"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <div className="relative flex flex-col items-center text-center">
                    <motion.div
                      whileHover={{ rotate: 5 }}
                      className="p-5 rounded-2xl bg-gradient-to-br from-primary/10 to-primary/5 mb-6 group-hover:from-primary/20 group-hover:to-primary/10 transition-all duration-300"
                    >
                      <Users className="w-12 h-12 text-primary" />
                    </motion.div>
                    <h3 className="text-xl font-bold mb-3 text-foreground">
                      {t("contact.ecosystem.title")}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {t("contact.ecosystem.description")}
                    </p>
                  </div>
                </motion.button>
              </motion.div>
            ) : (
            <div className="bg-card rounded-2xl border border-border p-8 md:p-12">
              <div className="flex items-center justify-between mb-8">
                <h3 className="text-2xl font-bold text-foreground">
                  {userType === "corporate" 
                    ? t("contact.corporate.title")
                    : userType === "startup"
                    ? t("contact.startup.title")
                    : t("contact.ecosystem.title")}
                </h3>
                <Button
                  variant="ghost"
                  onClick={() => setUserType(null)}
                  className="gap-2"
                >
                  <ArrowLeft className="h-4 w-4" />
                  {t("contact.form.back")}
                </Button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <Label htmlFor="name">{t("contact.form.name")} *</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder={t("contact.form.namePlaceholder")}
                    className={errors.name ? "border-destructive" : ""}
                  />
                  {errors.name && (
                    <p className="text-sm text-destructive mt-1">{errors.name}</p>
                  )}
                </div>

                <div>
                  <Label htmlFor="email">{t("contact.form.email")} *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder={t("contact.form.emailPlaceholder")}
                    className={errors.email ? "border-destructive" : ""}
                  />
                  {errors.email && (
                    <p className="text-sm text-destructive mt-1">{errors.email}</p>
                  )}
                </div>

                <div>
                  <Label htmlFor="company">{t("contact.form.company")} *</Label>
                  <Input
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    placeholder={t("contact.form.companyPlaceholder")}
                    className={errors.company ? "border-destructive" : ""}
                  />
                  {errors.company && (
                    <p className="text-sm text-destructive mt-1">{errors.company}</p>
                  )}
                </div>

                <div>
                  <Label htmlFor="message">{t("contact.form.message")} *</Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder={t("contact.form.messagePlaceholder")}
                    rows={6}
                    className={errors.message ? "border-destructive" : ""}
                  />
                  {errors.message && (
                    <p className="text-sm text-destructive mt-1">{errors.message}</p>
                  )}
                </div>

                <Button type="submit" size="lg" className="w-full">
                  {t("contact.form.submit")}
                </Button>
              </form>
            </div>
          )}
          </AnimatePresence>
        </div>
      </DialogContent>
    </Dialog>
  );
});

export default Contact;
